import { RefObject, useEffect, useLayoutEffect, useRef } from 'react';
import type { NavigateFunction } from 'react-router';
import type { useLazyGetChatDocumentsQuery } from '@/api';
import type { MatterChatNavigationState } from '@/ui/features/chat/matterChat/matterChatNavigationState';
import type { HandleSendMessageMeta } from '@/ui/features/chat/hooks/useChat';

type GetChatDocumentsTrigger = ReturnType<typeof useLazyGetChatDocumentsQuery>[0];

export type ChatPageLocationState = MatterChatNavigationState;

function selectedFileIdsDependencyKey(selectedFileIds: string[] | undefined): string {
  if (!selectedFileIds?.length) return '';
  return [...selectedFileIds].sort().join('\0');
}

export function useAutoSendInitialChatPrompt({
  isInitialPromptApplied,
  locationState,
  pathname,
  hasAutoSentInitialPromptRef,
  handleSendMessage,
  triggerGetChatDocuments,
  navigate,
  chatId,
}: {
  isInitialPromptApplied: boolean;
  locationState: ChatPageLocationState | undefined;
  pathname: string;
  hasAutoSentInitialPromptRef: RefObject<boolean>;
  handleSendMessage: (
    selectedFileIds?: string[],
    promptOverride?: string,
    forceChatId?: string,
    sendMeta?: HandleSendMessageMeta,
    selectedHistoryRowIds?: string[],
  ) => Promise<string | undefined>;
  triggerGetChatDocuments: GetChatDocumentsTrigger;
  navigate: NavigateFunction;
  chatId: string;
}) {
  const locationStateRef = useRef(locationState);
  const handleSendMessageRef = useRef(handleSendMessage);
  const triggerGetChatDocumentsRef = useRef(triggerGetChatDocuments);
  const navigateRef = useRef(navigate);

  useLayoutEffect(() => {
    locationStateRef.current = locationState;
    handleSendMessageRef.current = handleSendMessage;
    triggerGetChatDocumentsRef.current = triggerGetChatDocuments;
    navigateRef.current = navigate;
  }, [handleSendMessage, locationState, navigate, triggerGetChatDocuments]);

  const selectedFileIdsKey = selectedFileIdsDependencyKey(locationState?.selectedFileIds);

  useEffect(() => {
    let cancelled = false;

    async function runAutoSendInitialPrompt() {
      const state = locationStateRef.current;
      if (isInitialPromptApplied && state?.initialPrompt) {
        if (hasAutoSentInitialPromptRef.current) return;
        hasAutoSentInitialPromptRef.current = true;
        await handleSendMessageRef.current(
          state.selectedFileIds,
          undefined,
          undefined,
          undefined,
          state.selectedHistoryRowIds,
        );
        if (cancelled) return;
        await triggerGetChatDocumentsRef.current({ chatId }, false).unwrap();
        if (cancelled) return;

        navigateRef.current(pathname, {
          replace: true,
          state: {
            ...state,
            initialPrompt: undefined,
          },
        });
      }
    }

    void runAutoSendInitialPrompt();

    return () => {
      cancelled = true;
    };
  }, [
    chatId,
    hasAutoSentInitialPromptRef,
    isInitialPromptApplied,
    locationState?.initialPrompt,
    pathname,
    selectedFileIdsKey,
  ]);
}
